import React, { useState, useEffect, useRef } from 'react';
import { getCachedSymbolNamesSync, getCachedSymbolNames, normalizeSymbolNameEntry } from '../utils/symbolNamesCache';
import { Check, X } from 'lucide-react';

const MultiSymbolAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Type symbol symbols (comma-separated)...",
  className = "",
  disabled = false,
  maxSuggestions = 10
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [symbolMap, setSymbolMap] = useState({});
  const [invalidSymbols, setInvalidSymbols] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load symbol names on mount
  useEffect(() => {
    const loadSymbolNames = async () => {
      let symbols = getCachedSymbolNamesSync();
      if (!symbols || Object.keys(symbols).length === 0) {
        symbols = await getCachedSymbolNames();
      }
      if (symbols && Object.keys(symbols).length > 0) {
        setSymbolMap(symbols);
      }
    };
    loadSymbolNames();
  }, []);

  // Get current symbol being typed (last one after comma) - preserve case for company name search
  const getCurrentSymbol = () => {
    const parts = value.split(',');
    return parts[parts.length - 1].trim();
  };

  // Get current symbol in uppercase for validation
  const getCurrentSymbolUpper = () => {
    return getCurrentSymbol().toUpperCase();
  };

  // Get all symbols entered so far
  const getAllSymbols = () => {
    return value.split(',').map(t => t.trim()).filter(t => t.length > 0);
  };

  // Validate all symbols
  useEffect(() => {
    if (!value || value.trim() === '') {
      setInvalidSymbols([]);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const allSymbols = getAllSymbols();
    const invalid = allSymbols.filter(symbol => {
      if (!symbol.length || !symbolMap) return false;
      const hit = symbolMap[symbol] || Object.keys(symbolMap).find(k => k.toUpperCase() === symbol.toUpperCase());
      return !hit;
    });
    setInvalidSymbols(invalid);

    // Generate suggestions for current symbol being typed (search by both symbol and company name)
    const currentSymbol = getCurrentSymbol();
    const currentSymbolUpper = currentSymbol.toUpperCase();
    const currentSymbolLower = currentSymbol.toLowerCase();
    const currentSymbolOriginal = currentSymbol.trim();
    
    if (currentSymbol && symbolMap && Object.keys(symbolMap).length > 0) {
      const filtered = Object.entries(symbolMap)
        .map(([symbolId, raw]) => ({
          symbol: symbolId,
          meta: normalizeSymbolNameEntry(symbolId, raw)
        }))
        .filter(({ symbol, meta }) => {
          const idUpper = symbol.toUpperCase();
          const tickUpper = (meta.symbol || '').toUpperCase();
          const companyUpper = (meta.company_name || '').toUpperCase();
          const companyLower = (meta.company_name || '').toLowerCase();
          const exchUpper = (meta.exchange || '').toUpperCase();
          const symbolMatch =
            idUpper.startsWith(currentSymbolUpper) ||
            idUpper.includes(currentSymbolUpper) ||
            tickUpper.startsWith(currentSymbolUpper) ||
            tickUpper.includes(currentSymbolUpper);
          const exchangeMatch =
            exchUpper.includes(currentSymbolUpper) || exchUpper.startsWith(currentSymbolUpper);
          const companyMatch =
            companyUpper.startsWith(currentSymbolUpper) ||
            companyUpper.includes(currentSymbolUpper) ||
            companyLower.includes(currentSymbolLower);
          return symbolMatch || companyMatch || exchangeMatch;
        })
        .sort((a, b) => {
          const symbolAUpper = a.symbol.toUpperCase();
          const symbolBUpper = b.symbol.toUpperCase();
          const companyAUpper = (a.meta.company_name || '').toUpperCase();
          const companyBUpper = (b.meta.company_name || '').toUpperCase();
          if (symbolAUpper === currentSymbolUpper) return -1;
          if (symbolBUpper === currentSymbolUpper) return 1;
          if (symbolAUpper.startsWith(currentSymbolUpper) && !symbolBUpper.startsWith(currentSymbolUpper)) return -1;
          if (symbolBUpper.startsWith(currentSymbolUpper) && !symbolAUpper.startsWith(currentSymbolUpper)) return 1;
          if (companyAUpper.startsWith(currentSymbolUpper) && !companyBUpper.startsWith(currentSymbolUpper)) return -1;
          if (companyBUpper.startsWith(currentSymbolUpper) && !companyAUpper.startsWith(currentSymbolUpper)) return 1;
          if (symbolAUpper.includes(currentSymbolUpper) && !symbolBUpper.includes(currentSymbolUpper)) return -1;
          if (symbolBUpper.includes(currentSymbolUpper) && !symbolAUpper.includes(currentSymbolUpper)) return 1;
          return a.symbol.localeCompare(b.symbol);
        })
        .slice(0, maxSuggestions);
      
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && currentSymbol.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value, symbolMap, maxSuggestions]);

  const handleInputChange = (e) => {
    // Allow mixed case for company name searches
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);
  };

  const handleSelect = (symbol) => {
    const allSymbols = getAllSymbols();
    const currentSymbol = getCurrentSymbol();
    
    // Replace current symbol with selected one, adding comma before it if there are existing symbols
    const parts = value.split(',');
    const previousSymbols = parts.slice(0, -1).filter(p => p.trim().length > 0);
    
    // Add comma before the selected symbol only if there are previous symbols
    const newValue = previousSymbols.length > 0 
      ? previousSymbols.join(',') + ', ' + symbol
      : symbol;
    
    onChange(newValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && invalidSymbols.length > 0) {
        e.preventDefault();
        alert(`Invalid symbol symbols: ${invalidSymbols.join(', ')}. Please select valid symbols from suggestions.`);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex].symbol);
        } else if (suggestions.length === 1) {
          handleSelect(suggestions[0].symbol);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      case ',':
        // Allow comma to be typed
        break;
    }
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    const currentSymbol = getCurrentSymbol();
    if (suggestions.length > 0 && currentSymbol.length > 0) {
      setShowSuggestions(true);
    }
  };

  const allSymbols = getAllSymbols();
  const currentSymbol = getCurrentSymbolUpper();

  return (
    <div className="multi-symbol-autocomplete-container">
      <div className="multi-symbol-autocomplete-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          autoComplete="off"
        />
        {invalidSymbols.length > 0 && (
          <div className="multi-symbol-autocomplete-invalid-icon">
            <X size={18} color="red" />
          </div>
        )}
      </div>
      
      {allSymbols.length > 0 && (
        <div className="multi-symbol-autocomplete-badges">
          {allSymbols.map((symbol, idx) => {
            const resolvedKey =
              symbolMap &&
              (symbolMap[symbol] || Object.keys(symbolMap).find((k) => k.toUpperCase() === symbol.toUpperCase()));
            const isValid = !!resolvedKey;
            return (
              <span
                key={idx}
                className={`multi-symbol-autocomplete-badge ${isValid ? 'valid' : 'invalid'}`}
              >
                {symbol}
                {isValid ? (
                  <Check size={12} color="green" />
                ) : (
                  <X size={12} color="red" />
                )}
              </span>
            );
          })}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="multi-symbol-autocomplete-suggestions"
        >
          {suggestions.map((item, index) => (
            <div
              key={item.symbol}
              onClick={() => handleSelect(item.symbol)}
              className={`multi-symbol-autocomplete-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="multi-symbol-autocomplete-suggestion-meta">
                <div className="multi-symbol-autocomplete-suggestion-top">
                  <span className="multi-symbol-autocomplete-suggestion-symbol">{item.meta.symbol || '—'}</span>
                  {item.meta.exchange ? (
                    <span className="multi-symbol-autocomplete-suggestion-exchange">{item.meta.exchange}</span>
                  ) : null}
                </div>
                <div className="multi-symbol-autocomplete-suggestion-company">
                  {item.meta.company_name}
                </div>
              </div>
              {item.symbol === currentSymbol && (
                <Check size={16} color="green" />
              )}
            </div>
          ))}
        </div>
      )}

      {invalidSymbols.length > 0 && (
        <div className="multi-symbol-autocomplete-error">
          Invalid symbols: {invalidSymbols.join(', ')}
        </div>
      )}
    </div>
  );
};

export default MultiSymbolAutocomplete;

